import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2, RefreshCw, Save } from "lucide-react";
import { api } from "@/lib/api";
import { StepShell, Field, Chips, TArea } from "./WizardUI";
import { SAMPLE_CONTENT_TYPES } from "./constants";

export default function Step10FirstContent({ saved, onSave, onBack, onSkip, busy }) {
  const [ctype, setCtype] = useState(saved?.content_type || "welcome_post");
  const [text, setText] = useState(saved?.text || "");
  const [cost, setCost] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/onboarding/sample-content", { content_type: ctype });
      setText(data.text);
      setCost(data.normal_cost);
      setSavedDraft(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenBusy(false); }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/sample-content/save", { content_type: ctype, text });
      setSavedDraft(true);
      toast.success("Saved to your Library as a draft");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <StepShell step={10} title="Create your first content"
      subtitle="Let's put your setup to work — generate a real piece of content from your station profile."
      onBack={onBack} onContinue={() => onSave({ content_type: ctype, text, saved_draft: savedDraft })}
      onSkip={onSkip} busy={busy} continueLabel={text ? "Continue" : "Continue without content"}>
      <Field label="What should we create?">
        <Chips options={SAMPLE_CONTENT_TYPES} values={[ctype]} single
          onChange={(v) => { setCtype(v[0] || "welcome_post"); }} testidPrefix="onb-sample" />
      </Field>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={generate} disabled={genBusy} data-testid="onb-sample-generate-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black hover:bg-brand-amber/90 transition disabled:opacity-50">
          {genBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : text ? <RefreshCw className="h-3.5 w-3.5" /> : <Wand2 className="h-3.5 w-3.5" />}
          {text ? "Regenerate" : "Generate"}
        </button>
        <span className="text-[11px] text-emerald-300 font-mono" data-testid="onb-sample-cost">
          FREE during setup{cost ? ` (normally ${cost} credit${cost === 1 ? "" : "s"})` : ""}
        </span>
      </div>
      {text !== "" && (
        <>
          <Field label="Your content — edit freely">
            <TArea value={text} onChange={(e) => { setText(e.target.value); setSavedDraft(false); }}
              data-testid="onb-sample-text" style={{ minHeight: 180 }} />
          </Field>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={saveDraft} disabled={saving || savedDraft} data-testid="onb-sample-save-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-4 py-2 text-xs text-ink-secondary hover:text-ink-primary transition disabled:opacity-50">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {savedDraft ? "Saved as draft ✓" : "Save as draft"}
            </button>
            <a href="/dashboard/calendar" target="_blank" rel="noreferrer"
              className="inline-flex items-center rounded-md border border-line bg-bg-elevated px-4 py-2 text-xs text-ink-secondary hover:text-ink-primary transition">
              Schedule / publish from Calendar
            </a>
          </div>
        </>
      )}
    </StepShell>
  );
}
